import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import type {
  AdminMemberSearchData,
  CreateCareerBulkResult,
} from '../shared/dto/EntityRepository';
import {
  AlertPosition,
  AlertType,
} from '../shared/enum/EnumRepository';
import { useApiWrapper } from '../shared/hooks/useApi/apiWrapper';
import { useStandardAlertStore } from '../shared/store/portal/alertStore';
import { getAlertMessage } from '../shared/constants/alertMessages';
import {
  CAREER_BULK_FILE_MAX_SIZE,
  CAREER_BULK_FILE_MAX_SIZE_TEXT,
  CAREER_BULK_MAX_ROWS,
} from '../shared/constants/limits';
import { sizeCheckBig } from '../shared/util/fileSizeCheck';
import HeadlessBtn from '../shared/component/headless/button/HeadlessBtn';
import {
  BlackFitBtn,
  PaperBlurFitBtn,
} from '../shared/component/headless/button/BtnBody';
import { createCareerBulk } from './share/api/adminApi';
import CareerMemberSearch from './component/careers/CareerMemberSearch';
import CareerBulkPreview from './component/careers/CareerBulkPreview';
import CareerBulkResult from './component/careers/CareerBulkResult';
import { downloadCareerBulkTemplate } from './component/careers/buildCareerBulkTemplate';
import {
  CareerBulkSheetParseError,
  CareerBulkSheetParseResult,
  parseCareerBulkSheet,
} from './component/careers/parseCareerBulkSheet';

interface BulkPreviewState {
  fileName: string;
  targetMember: AdminMemberSearchData;
  result: CareerBulkSheetParseResult;
}

interface BulkResultState {
  targetMember: AdminMemberSearchData;
  result: CreateCareerBulkResult;
}

const AdminCareers: React.FC = () => {
  const [selectedMember, setSelectedMember] = useState<AdminMemberSearchData | null>(null);
  const [bulkPreview, setBulkPreview] = useState<BulkPreviewState | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkResultState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const enqueueAlert = useStandardAlertStore((state) => state.enqueueAlert);
  const withApiHandler = useApiWrapper();

  const showAlert = (content: string) => {
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content,
    });
  };

  const selectMember = (member: AdminMemberSearchData | null) => {
    setSelectedMember(member);
    setBulkPreview(null);
    setBulkResult(null);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedMember) return;
    if (!sizeCheckBig(file, CAREER_BULK_FILE_MAX_SIZE)) {
      showAlert(getAlertMessage('FILE_TOO_BIG_PREVIEW', {
        maxSize: CAREER_BULK_FILE_MAX_SIZE_TEXT,
      }));
      return;
    }

    try {
      const result = await parseCareerBulkSheet(file);
      if (result.totalCount === 0) {
        showAlert(getAlertMessage('MISSING_REQUIRED_FIELD'));
        return;
      }
      if (result.totalCount > CAREER_BULK_MAX_ROWS) {
        showAlert(getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', {
          maxCount: CAREER_BULK_MAX_ROWS,
        }));
        return;
      }
      setBulkResult(null);
      setBulkPreview({
        fileName: file.name,
        targetMember: selectedMember,
        result,
      });
    } catch (error) {
      showAlert(error instanceof CareerBulkSheetParseError
        ? error.message
        : getAlertMessage('INVALID_VALUE_REQUESTED'));
    }
  };

  const handleTemplateDownload = async () => {
    try {
      await downloadCareerBulkTemplate();
    } catch {
      showAlert(getAlertMessage('INVALID_VALUE_REQUESTED'));
    }
  };

  const handleConfirm = async () => {
    if (!bulkPreview || isSubmitting || bulkPreview.result.careerList.length === 0) return;
    setIsSubmitting(true);
    try {
      const response = await withApiHandler(
        () => createCareerBulk(String(bulkPreview.targetMember.memberId), {
          careerList: bulkPreview.result.careerList,
        }),
        ['create-career-bulk', bulkPreview.targetMember.memberId],
        { maxRetries: 1 },
      );
      if (!response) return;

      const results = [
        ...response.data.results,
        ...bulkPreview.result.invalidResults,
      ].sort((left, right) => left.rowNumber - right.rowNumber);
      const mergedResult: CreateCareerBulkResult = {
        totalCount: results.length,
        createdCount: results.filter((item) => item.status === 'CREATED').length,
        invalidCount: results.filter((item) => item.status === 'INVALID').length,
        results,
      };
      setBulkResult({
        targetMember: bulkPreview.targetMember,
        result: mergedResult,
      });
      setBulkPreview(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Header>
        <Title>Career import</Title>
        <Description>Select a member, validate an Excel file, and create careers in bulk.</Description>
      </Header>
      <Workspace>
        <ControlPanel>
          <CareerMemberSearch
            selectedMember={selectedMember}
            disabled={isSubmitting}
            onSelect={selectMember}
          />
          <Divider />
          <ActionTitle>Excel</ActionTitle>
          <ActionDescription>
            Download the template, fill the Careers sheet, then select the file.
          </ActionDescription>
          <Actions>
            <HeadlessBtn
              value="Download template"
              handleClick={() => { void handleTemplateDownload(); }}
              StyledBtn={PaperBlurFitBtn}
            />
            <HeadlessBtn
              value="Select Excel"
              handleClick={() => fileInputRef.current?.click()}
              disabled={!selectedMember || isSubmitting}
              StyledBtn={BlackFitBtn}
            />
          </Actions>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
          />
          {!selectedMember && <Hint>Select a target member before choosing a file.</Hint>}
        </ControlPanel>
        <ContentPanel>
          {bulkPreview ? (
            <CareerBulkPreview
              fileName={bulkPreview.fileName}
              targetMember={bulkPreview.targetMember}
              result={bulkPreview.result}
              isSubmitting={isSubmitting}
              onCancel={() => setBulkPreview(null)}
              onConfirm={() => { void handleConfirm(); }}
            />
          ) : bulkResult ? (
            <CareerBulkResult
              targetMember={bulkResult.targetMember}
              result={bulkResult.result}
              onDone={() => setBulkResult(null)}
            />
          ) : (
            <EmptyState>
              <EmptyTitle>No file selected</EmptyTitle>
              <EmptyDescription>
                Invalid rows are skipped. Valid rows are sent to the selected member only.
              </EmptyDescription>
            </EmptyState>
          )}
        </ContentPanel>
      </Workspace>
    </PageContainer>
  );
};

const PageContainer = styled.main`
  width: 96%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  margin-top: 8vh;
  margin-bottom: 5.8vh;
  overflow: hidden;
`;

const Header = styled.header`
  padding-bottom: 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const Description = styled.p`
  margin: 5px 0 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const Workspace = styled.div`
  min-height: 0;
  display: grid;
  flex: 1;
  grid-template-columns: minmax(300px, 36%) minmax(0, 1fr);
`;

const ControlPanel = styled.aside`
  min-width: 0;
  padding: 28px 30px 30px 0;
  border-right: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  overflow-y: auto;
  &::-webkit-scrollbar { display: none; }
`;

const ContentPanel = styled.section`
  min-width: 0;
  min-height: 0;
  padding: 28px 0 30px 30px;
  overflow: hidden;
`;

const Divider = styled.div`
  margin: 26px 0;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const ActionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ActionDescription = styled.p`
  margin: 7px 0 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const Hint = styled.div`
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const EmptyState = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
`;

const EmptyTitle = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const EmptyDescription = styled.div`
  max-width: 420px;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

export default AdminCareers;
